import { Field, Form, Formik, useFormikContext } from "formik";
import React, { useEffect, useMemo, useRef, useState, Fragment } from "react";
import AddButton from "../buttons/button";
import moment from "moment";
import errorToast from "../helper-components/error-toast";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { Dialog, Transition } from "@headlessui/react";
import CloseIcon from "../../assets/icons/close-icon";
import InputField from "../forms/input-field";
import SelectField from "../forms/select-field";
import { ChevronDownIcon, Package } from "lucide-react";

import {
  decimalNumberFn,
  minDate,
  positiveIntegerFn,
} from "../helper-components/helper-function";
import LoadingOverlay from "../helper-components/overlay-loading";
import { salesTaxCalAction } from "../../redux/actions/sales-action";

const SalesBatchesPanel = ({
  setOpen,
  itemId,
  setItemId,
  binLocationList = [],
  disName = "",
  disabled = false,
  itemValues = {},
  open,
  prefix = "gort",
  searchRef,
}) => {
  // OUTER formik (main screen form)
  const { setFieldValue: setOuterFieldValue } = useFormikContext();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [openBatchIndex, setOpenBatchIndex] = useState(null);
  const [newBatch, setNewBatch] = useState(false);

  const batchRef = useRef();
  const expiryRef = useRef();
  const binRef = useRef();
  const freeQtyRef = useRef();
  const mrpRef = useRef();
  const basePrRef = useRef();
  const disPerRef = useRef();

  const defaultSelectedItem = itemValues?.data?.find(
    (item) => itemId === item?.item_id
  );

  const defaultSelectedItemIndex = itemValues?.data?.findIndex(
    (item) => itemId === item?.item_id
  );

  const batches = defaultSelectedItem?.item_batches || [];

  // ✅ REQUIRED quantity must be stable and must NOT be overwritten by batch totals
  // Prefer the line item quantity (what user entered on main screen).
  const requiredQty = useMemo(() => {
    const q =
      defaultSelectedItem?.required_qty ??
      defaultSelectedItem?.item_required_quantity ??
      defaultSelectedItem?.quantity ?? // typical line item qty
      0;
    return Number(q || 0);
  }, [defaultSelectedItem]);

  // ✅ Allocated quantity = sum of batch quantities (changes as user adds/edits)
  const allocatedQty = useMemo(() => {
    return (batches || []).reduce(
      (sum, b) => sum + Number(b?.item_batch_quantity || 0),
      0
    );
  }, [batches]);

  const remainingQty = useMemo(() => {
    return Math.max(0, Number(requiredQty || 0) - Number(allocatedQty || 0));
  }, [requiredQty, allocatedQty]);

  const selectedBatchNumbers = useMemo(() => {
    return (batches || []).map((b) => b?.item_batch_number);
  }, [batches]);

  useEffect(() => {
    if (open) {
      // If there are no batches OR not fully allocated, go to Batch Info stage (and show Add Batch button).
      setNewBatch((batches?.length || 0) === 0);
      setOpenBatchIndex(null);
      setTimeout(() => batchRef.current?.focus(), 0);
    }
  }, [open]); // intentionally only on open

  const clampQty = (qty, maxAllowed) => {
    const q = Number(qty || 0);
    const m = Number(maxAllowed);

    if (maxAllowed === null || maxAllowed === undefined || Number.isNaN(m))
      return q;

    return Math.max(0, Math.min(q, m));
  };

  const getEditCarryQty = () => {
    if (typeof openBatchIndex !== "number") return 0;
    return Number(batches?.[openBatchIndex]?.item_batch_quantity || 0);
  };

  // Remaining limit while editing should include the qty of the batch being edited
  const getRemainingLimit = () => Number(remainingQty || 0) + getEditCarryQty();

  const getMaxAllowedQty = (availQty) => {
    const avail = Number(availQty);
    const remainingLimit = getRemainingLimit();

    if (availQty === null || availQty === undefined || Number.isNaN(avail)) {
      return remainingLimit;
    }
    return Math.min(avail, remainingLimit);
  };

  const showFirstErrorToast = (errors = {}) => {
    if (errors?.item_batch_number) return errorToast(errors.item_batch_number);
    if (errors?.item_exp_date) return errorToast(errors.item_exp_date);
    if (errors?.item_batch_quantity) return errorToast(errors.item_batch_quantity);
    if (errors?.item_batch_sales_rate) return errorToast(errors.item_batch_sales_rate);
    if (errors?.item_batch_unit_price) return errorToast(errors.item_batch_unit_price);
  };

  const validationSchema = Yup.object().shape({
    item_batch_number: Yup.string().required("Batch number is required"),

    item_exp_date: Yup.date().required("Expiration date is required"),

    item_batch_quantity: Yup.number()
      .typeError("Must be a number")
      .integer("Quantity must be an integer")
      .required("Sellable quantity is required")
      .moreThan(0, "Sellable quantity must be greater than 0")
      .test(
        "qty-not-more-than-available",
        "Quantity cannot exceed available quantity",
        function (value) {
          const availRaw = this?.parent?.item_batch_avail_quantity;
          const avail = Number(availRaw);

          // skip only if truly missing
          if (availRaw === null || availRaw === undefined || Number.isNaN(avail))
            return true;

          return Number(value || 0) <= avail;
        }
      )
      .test(
        "qty-not-more-than-remaining",
        "Quantity cannot exceed remaining required quantity",
        function (value) {
          const remainingLimit = getRemainingLimit(); // includes edit carry qty
          return Number(value || 0) <= Number(remainingLimit || 0);
        }
      ),

    item_batch_sales_rate: Yup.number()
      .typeError("Must be a number")
      .required("sales rate is required")
      .moreThan(0, "sales rate must be greater than 0"),

    item_batch_unit_price: Yup.number()
      .typeError("Must be a number")
      .required("MRP is required")
      .moreThan(0, "MRP must be greater than 0"),
    item_batch_free_quantity: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue == null ? 0 : Number(value)
      )
      .integer("Quantity must be an integer")
      .min(0),

  });

  const today = new Date();
  const sixMonthsLater = new Date(today.setMonth(today.getMonth() + 6));

  const baseInitialValues = useMemo(() => {
    return {
      item_id: defaultSelectedItem?.item_id,

      item_batch_number: "",
      item_batch_avail_quantity: 0,

      item_batch_quantity: remainingQty > 0 ? remainingQty : "",
      item_batch_free_quantity: "",

      item_batch_total_effective_qauntity: 0,
      item_batch_sales_rate: "",
      item_batch_total_base_sales_rate: 0,

      item_batch_for_qauntity: "",
      item_batch_for_free_qauntity: "",
      item_batch_scheme_discount_percentage: "",
      item_batch_scheme_discount_amount: 0,
      item_batch_scheme_sales_rate: 0,

      item_batch_discount_percentage: 0,
      item_batch_gen_discount_amount: 0,
      item_batch_discounted_sales_rate: "",
      item_batch_discount_amount: 0,

      item_batch_tax_percentage: "",
      item_batch_tax_amount: 0,
      item_batch_final_sales_rate: 0,
      item_batch_unit_price: "",
      item_batch_total_sales_rate: 0,
      item_batch_margin_percentage: 0,

      item_batch_cgst_percentage: "",
      item_batch_sgst_percentage: "",
      item_batch_igst_percentage: "",
      item_batch_sgst_amount: 0,
      item_batch_cgst_amount: 0,
      item_batch_igst_amount: 0,
      item_batch_purhcase_rate_before_tax: 0,

      item_exp_date: moment(sixMonthsLater).format("YYYY-MM-DD"),
      item_mfg_date: "2023/12/02",

      from_bin_location: "",
      to_bin_location: "receiving-bin",
      to_bin_id: binLocationList?.find(
        (bin) => bin?.actualLabel === "receiving-bin"
      )?.value,
      from_bin_id: "",
      gst_type: itemValues?.is_inter_state === true ? "Inter" : "Intra",
      itemBatchList: defaultSelectedItem?.itemBatchList || [],
    };
  }, [
    defaultSelectedItem?.item_id,
    defaultSelectedItem?.itemBatchList,
    binLocationList,
    remainingQty,
    itemValues?.is_inter_state,
  ]);
  const handleQuantityChange = async (
    qty,
    pr = 0,
    dis = 0,
    item_batch_for_qauntity = 0,
    item_batch_for_free_qauntity = 0,
    item_batch_free_quantity = 0,
    setValues,
    values,
    schemePer = 0,
    item_batch_unit_price = 0,
    gst_type = "Intra"
  ) => {
    const updatedData = { ...values };
    const effectiveGstType = gst_type || values?.gst_type || (itemValues?.is_inter_state === true ? "Inter" : "Intra");

    const denominator =
      parseInt(item_batch_for_qauntity || 0) +
      parseInt(item_batch_for_free_qauntity || 0);

    const item_batch_scheme_discount_percentage = schemePer
      ? schemePer
      : denominator > 0
      ? parseFloat(
          (parseInt(item_batch_for_free_qauntity || 0) / denominator) * 100
        ).toFixed(2)
      : 0;

    const item_batch_total_base_sales_rate =
      parseFloat(pr || 0) * parseInt(qty || 0);

    const item_batch_scheme_discount_amount =
      parseFloat(
        (item_batch_scheme_discount_percentage / 100) *
          item_batch_total_base_sales_rate
      ).toFixed(2) || 0;

    const item_batch_scheme_sales_rate =
      (
        parseFloat(item_batch_total_base_sales_rate || 0) -
        parseFloat(item_batch_scheme_discount_amount || 0)
      ).toFixed(2) || 0;

    const item_batch_gen_discount_amount =
      parseFloat((dis || 0) * item_batch_scheme_sales_rate).toFixed(2) ?? 0;

    const item_batch_discounted_sales_rate =
      parseFloat(
        item_batch_scheme_sales_rate - item_batch_gen_discount_amount
      )?.toFixed(2) || 0;

    const item_batch_total_effective_qauntity =
      parseInt(qty || 0) + parseInt(item_batch_free_quantity || 0);

    setLoading(true);

    dispatch(
      salesTaxCalAction(
        {
          item_id: defaultSelectedItem?.item_id,
          purchase_rate:
            item_batch_scheme_sales_rate -
            item_batch_scheme_sales_rate * (dis || 0),
          is_inter_state: effectiveGstType === "Inter",
        },
        (resp) => {
          setLoading(false);

          const data = resp?.data || {};
          const {
            item_tax_amount,
            item_total_tax_percentage,
            item_batch_cgst_percentage,
            item_batch_sgst_percentage,
            item_batch_igst_percentage,
            item_batch_sgst_amount,
            item_batch_cgst_amount,
            item_batch_igst_amount,
          } = data;

          const tax = +item_tax_amount || 0;
          const sgst = +item_batch_sgst_amount || +(tax / 2)?.toFixed(2);
          const cgst = +item_batch_cgst_amount || +(tax / 2)?.toFixed(2);
          const igst = +item_batch_igst_amount || +(tax).toFixed(2);

          const item_batch_total_sales_rate = parseFloat(
            parseFloat(item_batch_discounted_sales_rate) + parseFloat(tax)
          ).toFixed(2);

          const item_batch_final_sales_rate = parseFloat(
            (Number(item_batch_total_sales_rate || 0) || 0) /
              (Number(item_batch_total_effective_qauntity || 1) || 1)
          ).toFixed(2);

          const item_batch_margin_percentage =
            (
              ((parseFloat(item_batch_unit_price || 0) -
                parseFloat(item_batch_final_sales_rate || 0)) /
                (parseFloat(item_batch_final_sales_rate || 1) || 1)) *
              100
            ).toFixed(2) || 0;

          const item_batch_purhcase_rate_before_tax =
            item_batch_total_effective_qauntity > 0
              ? (
                  parseFloat(item_batch_final_sales_rate || 0) -
                  parseFloat(tax) / item_batch_total_effective_qauntity
                ).toFixed(2)
              : 0;

          setValues({
            ...updatedData,
            item_batch_quantity: +qty || "",
            item_batch_free_quantity: Number(item_batch_free_quantity) || 0,
            item_batch_total_effective_qauntity,

            item_batch_sales_rate: pr || "",
            item_batch_total_base_sales_rate,

            item_batch_for_qauntity: item_batch_for_qauntity || "",
            item_batch_for_free_qauntity: item_batch_for_free_qauntity || "",

            item_batch_scheme_discount_percentage:
              item_batch_scheme_discount_percentage || "",
            item_batch_scheme_discount_amount: item_batch_scheme_discount_amount || 0,
            item_batch_scheme_sales_rate: item_batch_scheme_sales_rate || "",

            item_batch_discount_percentage: (dis || 0) * 100 || 0,
            item_batch_gen_discount_amount: item_batch_gen_discount_amount,
            item_batch_discounted_sales_rate: item_batch_discounted_sales_rate || 0,

            item_batch_discount_amount: (
              parseFloat(item_batch_gen_discount_amount || 0) +
              parseFloat(item_batch_scheme_discount_amount || 0)
            ).toFixed(2),

            item_batch_tax_percentage: item_total_tax_percentage,
            item_batch_tax_amount: parseFloat(tax).toFixed(2),

            item_batch_total_sales_rate,
            item_batch_unit_price: item_batch_unit_price || "",

            item_batch_final_sales_rate: item_batch_final_sales_rate || 0,
            item_batch_margin_percentage,

            item_batch_sgst_amount: parseFloat(sgst).toFixed(2),
            item_batch_cgst_amount: parseFloat(cgst).toFixed(2),
            item_batch_igst_amount: parseFloat(igst).toFixed(2),
            item_batch_cgst_percentage: item_batch_cgst_percentage != null ? parseFloat(item_batch_cgst_percentage) : updatedData.item_batch_cgst_percentage,
            item_batch_sgst_percentage: item_batch_sgst_percentage != null ? parseFloat(item_batch_sgst_percentage) : updatedData.item_batch_sgst_percentage,
            item_batch_igst_percentage: item_batch_igst_percentage != null ? parseFloat(item_batch_igst_percentage) : updatedData.item_batch_igst_percentage,
            item_batch_purhcase_rate_before_tax,
          });
        },
        () => setLoading(false)
      )
    );
  };

  const recomputeInvoiceTotals = (updatedData) => {
    const raw_total_amount = updatedData.reduce(
      (sum, item) => sum + (parseFloat(item.item_total_amount) || 0),
      0
    );

    const total_tax = updatedData.reduce(
      (sum, item) => sum + (parseFloat(item.item_tax_amount) || 0),
      0
    );

    const total_discount = updatedData.reduce(
      (sum, item) => sum + (parseFloat(item.item_discount_amount) || 0),
      0
    );

    const sub_total = (raw_total_amount - total_tax + total_discount).toFixed(2);
    const total_amount = Math.round(raw_total_amount).toFixed(2);
    const round_off_value = (Math.round(raw_total_amount) - raw_total_amount).toFixed(2);

    setOuterFieldValue(`${prefix}_sub_total`, sub_total);
    setOuterFieldValue(`${prefix}_total_gst`, total_tax);
    setOuterFieldValue(`${prefix}_total_discount`, total_discount);
    setOuterFieldValue(`${prefix}_total_amount`, total_amount);
    setOuterFieldValue(`${prefix}_round_off_value`, round_off_value);

    setOuterFieldValue(`${prefix}_tcs_or_tds_amount`, 0);
    setOuterFieldValue(`${prefix}_tcs_or_tds_percentage_id`, "");
    setOuterFieldValue(`${prefix}_tcs_or_tds_percentage`, 0);
    setOuterFieldValue(`${prefix}_tds_tcs_type`, "");
    setOuterFieldValue(`${prefix}_is_invoice_dis_percentage`, true);
    setOuterFieldValue(`${prefix}_invoice_discount`, "");
    setOuterFieldValue(`${prefix}_invoice_discount_percentage`, "");
    setOuterFieldValue(`header_inv_dis_per`, "");
  };

  const buildUpdatedItemRowFromBatches = (item_batches) => {
    const sumQty = item_batches.reduce(
      (sum, b) => sum + (parseFloat(b.item_batch_quantity) || 0),
      0
    );
    const totalDiscount = item_batches.reduce(
      (sum, b) => sum + (parseFloat(b.item_batch_discount_amount) || 0),
      0
    );
    const totalTax = item_batches.reduce(
      (sum, b) => sum + (parseFloat(b.item_batch_tax_amount) || 0),
      0
    );
    const totalCGSTTax = item_batches.reduce(
      (sum, b) => sum + (parseFloat(b.item_batch_cgst_amount) || 0),
      0
    );
    const totalSGSTTax = item_batches.reduce(
      (sum, b) => sum + (parseFloat(b.item_batch_sgst_amount) || 0),
      0
    );
    const totalIGSTTax = item_batches.reduce(
      (sum, b) => sum + (parseFloat(b.item_batch_igst_amount) || 0),
      0
    );
    const totalAmt = item_batches.reduce(
      (sum, b) => sum + (parseFloat(b.item_batch_total_sales_rate) || 0),
      0
    );
    const totalFreeQty = item_batches.reduce(
      (sum, b) => sum + (parseFloat(b.item_batch_free_quantity) || 0),
      0
    );

    // ✅ IMPORTANT: Do NOT overwrite the line item required qty field
    // Keep `quantity` as-is (what user entered) and store allocated sum separately.
    const stableLineQty =
      defaultSelectedItem?.quantity ?? defaultSelectedItem?.required_qty ?? requiredQty;

    return {
      ...defaultSelectedItem,
      item_batches,

      // keep required qty stable
      quantity: stableLineQty,

      // store allocation separately
      allocated_quantity: sumQty,

      item_free_quantity: +totalFreeQty,
      gort_total_discount: +totalDiscount,
      item_discount_amount: +totalDiscount,
      item_tax_amount: +Number(totalTax || 0).toFixed(2),

      item_gst: item_batches?.[0]?.item_batch_tax_percentage || 0,
      item_sgst: +totalSGSTTax?.toFixed(2),
      item_cgst: +totalCGSTTax?.toFixed(2),
      item_igst: +totalIGSTTax?.toFixed(2),

      // pack size should follow required qty (stable), not allocated qty
      item_pack_size: (defaultSelectedItem?.base_quantity || 0) * Number(stableLineQty || 0),

      item_total_amount: totalAmt.toFixed(2),
    };
  };

  const handleDeleteBatch = (batchIndex) => {
    const updatedBatches = [...(batches || [])];
    updatedBatches.splice(batchIndex, 1);

    const updatedDataRow = buildUpdatedItemRowFromBatches(updatedBatches);

    setOuterFieldValue(`data.${defaultSelectedItemIndex}`, updatedDataRow);

    const updatedData = [...(itemValues?.data || [])];
    updatedData[defaultSelectedItemIndex] = updatedDataRow;
    recomputeInvoiceTotals(updatedData);

    setOpenBatchIndex(null);
    setNewBatch(false);
  };

  const startAddBatch = (setValues, currentValues) => {
    setOpenBatchIndex(null);
    setNewBatch(true);

    setValues({
      ...baseInitialValues,
      itemBatchList:
        currentValues?.itemBatchList || baseInitialValues?.itemBatchList || [],
      item_batch_quantity: remainingQty > 0 ? remainingQty : "",
    });

    setTimeout(() => batchRef.current?.focus(), 0);
  };

  const hydrateEditBatch = (batch, itemBatchList) => {
    const found = (itemBatchList || []).find(
      (x) => x?.item_batch_number === batch?.item_batch_number
    );

    const avail = Number(found?.item_quantity ?? batch?.item_batch_avail_quantity ?? 0);
    const gst_type =
      batch?.gst_type ??
      (batch?.is_inter_state === true ? "Inter" : "Intra");

    return {
      ...batch,
      itemBatchList: itemBatchList || [],
      item_batch_avail_quantity: avail,
      item_exp_date: batch?.item_exp_date
        ? moment(batch.item_exp_date).format("YYYY-MM-DD")
        : moment(sixMonthsLater).format("YYYY-MM-DD"),
      gst_type,
    };
  };

  const applySelectedBatch = (sel, values, setValues, setFieldValue) => {
    const avail = Number(sel?.item_quantity || 0);

    // ✅ dropdown already hides 0 qty, but keep safety
    if (avail <= 0) {
      errorToast("This batch has 0 available quantity. Select another batch.");
      setFieldValue("item_batch_number", "");
      setFieldValue("item_batch_avail_quantity", 0);
      return;
    }

    const mrp = Number(sel?.item_unit_price ?? values?.item_batch_unit_price ?? 0);
    const baseSp = Number(sel?.item_selling_price ?? values?.item_batch_sales_rate ?? 0);

    const maxAllowed = getMaxAllowedQty(avail);

    const desiredRaw =
      values?.item_batch_quantity === "" || values?.item_batch_quantity == null
        ? remainingQty
        : Number(values?.item_batch_quantity || 0);

    const desired = clampQty(desiredRaw, maxAllowed);

    if (Number(desiredRaw || 0) > maxAllowed) {
      errorToast(`Quantity cannot exceed ${maxAllowed}`);
    }

    const nextValues = {
      ...values,
      item_batch_number: sel?.item_batch_number,
      item_batch_avail_quantity: avail,

      to_bin_id: sel?.to_bin_id || values?.to_bin_id || "",
      from_bin_location:
        sel?.from_bin_location || sel?.item_rack_location || "",

      item_exp_date: sel?.item_exp_date
        ? moment(sel.item_exp_date).format("YYYY-MM-DD")
        : values?.item_exp_date,

      item_batch_unit_price: mrp,
      item_batch_sales_rate: baseSp,

      item_batch_quantity: desired,
    };

    setValues(nextValues);

    handleQuantityChange(
      desired,
      baseSp,
      Number(nextValues?.item_batch_discount_percentage || 0) / 100,
      parseInt(nextValues?.item_batch_for_qauntity || 0),
      parseInt(nextValues?.item_batch_for_free_qauntity || 0),
      parseInt(nextValues?.item_batch_free_quantity || 0),
      setValues,
      nextValues,
      Number(nextValues?.item_batch_scheme_discount_percentage || 0),
      mrp,
      nextValues?.gst_type
    );
  };

  const innerOnSubmit = (values) => {
    const avail = Number(values?.item_batch_avail_quantity || 0);
    const qty = Number(values?.item_batch_quantity || 0);

    if (!values?.item_batch_number) {
      errorToast("Batch number is required");
      return;
    }
    if (avail <= 0) {
      errorToast("Selected batch has 0 available quantity.");
      return;
    }

    const maxAllowed = getMaxAllowedQty(avail);
    if (qty > maxAllowed) {
      errorToast(`Quantity cannot exceed ${maxAllowed}`);
      return;
    }

    const item_batches = [...(batches || [])];

    if (
      typeof openBatchIndex === "number" &&
      openBatchIndex >= 0 &&
      openBatchIndex < item_batches.length
    ) {
      item_batches[openBatchIndex] = values;
    } else {
      item_batches.push(values);
    }

    const updatedDataRow = buildUpdatedItemRowFromBatches(item_batches);

    setOuterFieldValue(`data.${defaultSelectedItemIndex}`, updatedDataRow);

    const updatedData = [...(itemValues?.data || [])];
    updatedData[defaultSelectedItemIndex] = updatedDataRow;
    recomputeInvoiceTotals(updatedData);

    setNewBatch(false);
    setOpenBatchIndex(null);
  };

  const defaultInputClass =
    "min-h-[42px] rounded-[8px] px-2 bg-[var(--vert-input-bg)] text-[var(--vert-input-color)] text-[length:var(--vert-input-font-size)] font-[var(--vert-input-font-weight)] rounded input-field-no-spin-buttons placeholder:text-[var(--vert-input-place-color)] placeholder:text-[length:var(--vert-input-place-font-size)] placeholder:font-[var(--vert-input-place-font-weight)] focus:outline-none focus:border-[var(--vert-input-focus)] focus:text-[var(--vert-input-color)] focus:bg-[var(--vert-input-bg)]";
  const labelClass =
    "whitespace-nowrap flex text-[var(--vert-input-label-color)] text-[length:var(--vert-input-label-font-size)] font-[var(--vert-input-label-font-weight)]";
  const errorBorder = "border border-[var(--vert-input-error-border)]";
  const inputBorder = "border border-[var(--vert-input-border)]";

  const toggleBatch = (index) => {
    setOpenBatchIndex((prev) => (prev === index ? null : index));
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => setOpen(false)}
      >
        <div className="fixed inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
          </Transition.Child>

          <div className="fixed inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="h-full relative w-[400px] sm:w-[600px] bg-white overflow-y-auto rounded-tl-lg rounded-bl-lg shadow-xl">
                <Formik
                  enableReinitialize={true}
                  initialValues={baseInitialValues}
                  validationSchema={validationSchema}
                  onSubmit={innerOnSubmit}
                >
                  {({
                    values,
                    handleSubmit,
                    errors,
                    setFieldValue,
                    setValues,
                  }) => {
                    // Dropdown selected value label (must show avail qty too)
                    const selectedDropdownValue = values?.item_batch_number
                      ? {
                          value: values?.item_batch_number,
                          label: (
                            <span className="flex flex-col">
                              <span>
                                Avail Qty:{" "}
                                <b>{values?.item_batch_avail_quantity || 0}</b>
                              </span>
                              <span>Btch Num: {values?.item_batch_number}</span>
                            </span>
                          ),
                        }
                      : null;

                    // ✅ Clear dropdown:
                    // - Hide 0 qty
                    // - Hide already selected batches
                    const batchOptions =
                      (values?.itemBatchList || [])
                        .filter((b) => Number(b?.item_quantity || 0) > 0)
                        .filter(
                          (b) =>
                            !selectedBatchNumbers?.includes(b?.item_batch_number)
                        )
                        .sort((a, b) => {
                          const da = new Date(a?.item_exp_date || 0).getTime();
                          const db = new Date(b?.item_exp_date || 0).getTime();
                          return da - db;
                        })
                        .map((item) => ({
                          label: (
                            <div className="flex flex-col">
                              <div className="flex justify-between">
                                <span>Btch: {item?.item_batch_number}</span>
                                <span className="font-medium">
                                  Avail: {item?.item_quantity}
                                </span>
                              </div>
                              <div className="text-[12px] opacity-70 flex justify-between">
                                <span>
                                  Exp:{" "}
                                  {moment(item?.item_exp_date).format(
                                    "DD-MM-YYYY"
                                  )}
                                </span>
                                <span>MRP: ₹{item?.item_unit_price}</span>
                                <span>SP: ₹{item?.item_selling_price}</span>
                              </div>
                            </div>
                          ),
                          value: item?.item_batch_number,
                          item_batch_number: item?.item_batch_number,
                          to_bin_id: item?.to_bin_id,
                          from_bin_location: item?.item_rack_location,
                          item_unit_price: item?.item_unit_price,
                          item_selling_price: item?.item_selling_price,
                          item_quantity: item?.item_quantity,
                          item_exp_date: item?.item_exp_date,
                        })) || [];

                    const maxAllowedNow = getMaxAllowedQty(
                      values?.item_batch_avail_quantity || 0
                    );

                    return (
                      <Form className="h-full relative">
                        {/* Header */}
                        <div
                          className="h-[61px] bg-[var(--slide-panel-head-bg)] px-4 flex items-center justify-between border-b"
                          style={{ borderColor: "var(--slide-panel-border)" }}
                        >
                          <div
                            style={{
                              maxHeight: "32px",
                              color: "var(--slide-panel-h-fg)",
                              fontSize: "var(--slide-panel-h-fs)",
                              fontWeight: "var(--slide-panel-h-fw)",
                            }}
                            className="min-w-[75%]"
                          >
                            {newBatch
                              ? typeof openBatchIndex === "number"
                                ? "Update Batch"
                                : "Add New Batch"
                              : "Batch Info"}
                          </div>

                          <AddButton
                            onclkFn={() => setOpen(false)}
                            icon={
                              <CloseIcon
                                width="12px"
                                height="12px"
                                fill="var(--close-button-fg)"
                              />
                            }
                            text={
                              <span>
                                Close <span className="opacity-50">(X)</span>{" "}
                              </span>
                            }
                            className="flex h-[32px] items-center gap-2 bg-[var(--close-button-bg)] border-radius px-3 text-[var(--close-button-fg)]"
                          />
                        </div>

                        {/* Required vs Allocated vs Remaining (Required MUST NOT change) */}
                        <div className="px-4 pt-3">
                          <div className="border border-[var(--slide-panel-border)] rounded-[8px] p-3 flex justify-between">
                            <span>
                              Required: <b>{requiredQty}</b>
                            </span>
                            <span>
                              Allocated: <b>{allocatedQty}</b>
                            </span>
                            <span>
                              Remaining:{" "}
                              <b
                                style={{
                                  color:
                                    remainingQty > 0 ? "#b45309" : "#16a34a",
                                }}
                              >
                                {remainingQty}
                              </b>
                            </span>
                          </div>
                        </div>

                        {newBatch ? (
                          // -------- Add / Edit Batch Form --------
                          <div
                            style={{
                              height: `calc(100% - 12rem)`,
                              minHeight: `calc(100% - 12rem)`,
                            }}
                            className="px-4 pt-4 relative"
                          >
                            <div className="h-full w-full flex flex-col gap-2 overflow-y-auto justify-between">
                              <div className="flex flex-col gap-2">
                                <SelectField
                                  key={`batchSel_${values?.item_batch_number}_${values?.item_batch_avail_quantity}`}
                                  general_color={false}
                                  isDisabled={disabled}
                                  selectClass="w-full"
                                  errMsgContainerClass="hidden"
                                  id={`item_batch_number`}
                                  isContainer={false}
                                  defaultValue={selectedDropdownValue}
                                  options={batchOptions}
                                  name={`item_batch_number`}
                                  placeholder={"Select Batch"}
                                  handleSelected={(sel) => {
                                    if (!sel) return;
                                    applySelectedBatch(
                                      sel,
                                      values,
                                      setValues,
                                      setFieldValue
                                    );
                                  }}
                                />

                                <div className="text-[12px] opacity-70">
                                  Available:{" "}
                                  <b>{values?.item_batch_avail_quantity || 0}</b>
                                  {"  |  "}
                                  Max allowed now: <b>{maxAllowedNow}</b>
                                </div>

                                <div className="flex gap-5">
                                  <InputField
                                    inputRef={expiryRef}
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    type="date"
                                    isRequired={true}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={false}
                                    name="item_exp_date"
                                    placeholder="Expiry Date"
                                    label="Expiry Date"
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                    min={minDate}
                                    onKeyDown={(e) => {
                                      if (e.key === "Tab" || e.key === "Enter") {
                                        e.preventDefault();
                                        binRef.current?.focus();
                                      }
                                    }}
                                  />

                                  <SelectField
                                    ref={binRef}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    isDisabled={disabled}
                                    placeholder="Select Bin"
                                    options={binLocationList}
                                    label="From Bin"
                                    id={`from_bin_location`}
                                    name={`from_bin_location`}
                                    isSearchable={true}
                                    isRequired={false}
                                    defaultValue={{
                                      label: values?.from_bin_location ?? "",
                                      value: "",
                                    }}
                                    handleSelected={(selectedoption) => {
                                      setFieldValue(
                                        `from_bin_location`,
                                        selectedoption?.actualLabel
                                      );
                                    }}
                                    errMsgContainerClass="hidden"
                                  />
                                </div>

                                <div className="flex gap-5">
                                  <InputField
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    type="number"
                                    isRequired={true}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={disabled || !values?.item_batch_number}
                                    name="item_batch_quantity"
                                    placeholder="Quantity"
                                    label="Quantity"
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                    handleChange={(e) => {
                                      if (!values?.item_batch_number) return;

                                      const raw = e.target.value;
                                      if (raw === "") {
                                        setFieldValue("item_batch_quantity", "");
                                        return;
                                      }

                                      let q = parseInt(raw, 10);
                                      if (Number.isNaN(q)) return;

                                      const maxAllowed = getMaxAllowedQty(
                                        values?.item_batch_avail_quantity || 0
                                      );
                                      q = clampQty(q, maxAllowed);
                                      setFieldValue("item_batch_quantity", q);
                                    }}
                                    onKeyDown={(e) => {
                                      positiveIntegerFn(e);
                                      if (e.key === "Tab" || e.key === "Enter") {
                                        e.preventDefault();

                                        if (!values?.item_batch_number) {
                                          errorToast("Select a batch first");
                                          return;
                                        }

                                        const maxAllowed = getMaxAllowedQty(
                                          values?.item_batch_avail_quantity || 0
                                        );

                                        const raw = parseInt(
                                          e.currentTarget.value || 0,
                                          10
                                        );
                                        const q = clampQty(raw, maxAllowed);

                                        if (raw > maxAllowed) {
                                          errorToast(
                                            `Quantity cannot exceed ${maxAllowed}`
                                          );
                                        }

                                        setFieldValue("item_batch_quantity", q);

                                        handleQuantityChange(
                                          q,
                                          +values?.item_batch_sales_rate || 0,
                                          Number(
                                            values?.item_batch_discount_percentage || 0
                                          ) / 100,
                                          parseInt(values?.item_batch_for_qauntity || 0),
                                          parseInt(
                                            values?.item_batch_for_free_qauntity || 0
                                          ),
                                          parseInt(values?.item_batch_free_quantity || 0),
                                          setValues,
                                          values,
                                          Number(
                                            values?.item_batch_scheme_discount_percentage || 0
                                          ),
                                          Number(values?.item_batch_unit_price || 0)
                                        );

                                        freeQtyRef.current?.focus();
                                      }
                                    }}
                                    onBlur={(e) => {
                                      if (!values?.item_batch_number) return;

                                      const maxAllowed = getMaxAllowedQty(
                                        values?.item_batch_avail_quantity || 0
                                      );
                                      const raw = parseInt(e.currentTarget.value || 0, 10);
                                      const q = clampQty(raw, maxAllowed);

                                      if (raw > maxAllowed) {
                                        errorToast(`Quantity cannot exceed ${maxAllowed}`);
                                      }

                                      setFieldValue("item_batch_quantity", q);

                                      handleQuantityChange(
                                        q,
                                        +values?.item_batch_sales_rate || 0,
                                        Number(values?.item_batch_discount_percentage || 0) / 100,
                                        parseInt(values?.item_batch_for_qauntity || 0),
                                        parseInt(values?.item_batch_for_free_qauntity || 0),
                                        parseInt(values?.item_batch_free_quantity || 0),
                                        setValues,
                                        values,
                                        Number(values?.item_batch_scheme_discount_percentage || 0),
                                        Number(values?.item_batch_unit_price || 0),
                                        values?.gst_type
                                      );
                                    }}
                                  />

                                  <InputField
                                    inputRef={freeQtyRef}
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    type="number"
                                    isRequired={false}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={disabled || !values?.item_batch_number}
                                    name="item_batch_free_quantity"
                                    placeholder="Free Qty"
                                    label="Free Qty"
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                    onKeyDown={(e) => {
                                      positiveIntegerFn(e);
                                      if (e.key === "Tab" || e.key === "Enter") {
                                        e.preventDefault();
                                        mrpRef.current?.focus();
                                      }
                                    }}
                                    onBlur={(e) => {
                                      const value = +(
                                        e.currentTarget.value || 0
                                      );

                                      handleQuantityChange(
                                        values?.item_batch_quantity || 0,
                                        +values?.item_batch_sales_rate || 0,
                                        Number(values?.item_batch_discount_percentage || 0) / 100,
                                        parseInt(values?.item_batch_for_qauntity || 0),
                                        parseInt(values?.item_batch_for_free_qauntity || 0),
                                        value || 0,
                                        setValues,
                                        values,
                                        Number(values?.item_batch_scheme_discount_percentage || 0),
                                        Number(values?.item_batch_unit_price || 0),
                                        values?.gst_type
                                      );
                                    }}
                                  />
                                </div>

                                <div className="flex gap-5">
                                  <InputField
                                    inputRef={mrpRef}
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    type="number"
                                    isRequired={true}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={disabled || !values?.item_batch_number}
                                    name="item_batch_unit_price"
                                    placeholder="MRP"
                                    label="MRP (Per 1 Qty)"
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                    onKeyDown={(e) => {
                                      decimalNumberFn(e);
                                      if (e.key === "Tab" || e.key === "Enter") {
                                        e.preventDefault();
                                        basePrRef.current?.focus();
                                      }
                                    }}
                                  />

                                  <InputField
                                    inputRef={basePrRef}
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    type="number"
                                    isRequired={true}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={disabled || !values?.item_batch_number}
                                    name="item_batch_sales_rate"
                                    placeholder="Base SP"
                                    label="Base SP (Per 1 Qty)"
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                    onKeyDown={(e) => {
                                      decimalNumberFn(e);
                                      if (e.key === "Tab" || e.key === "Enter") {
                                        e.preventDefault();
                                        disPerRef.current?.focus();
                                      }
                                    }}
                                    onBlur={(e) => {
                                      const value = Number(e.currentTarget.value || 0);

                                      handleQuantityChange(
                                        +values?.item_batch_quantity || 0,
                                        value,
                                        Number(values?.item_batch_discount_percentage || 0) / 100,
                                        parseInt(values?.item_batch_for_qauntity || 0),
                                        parseInt(values?.item_batch_for_free_qauntity || 0),
                                        parseInt(values?.item_batch_free_quantity || 0),
                                        setValues,
                                        values,
                                        Number(values?.item_batch_scheme_discount_percentage || 0),
                                        Number(values?.item_batch_unit_price || 0),
                                        values?.gst_type
                                      );
                                    }}
                                  />
                                </div>

                                <div className="flex gap-5">
                                  <InputField
                                    inputRef={disPerRef}
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    type="number"
                                    isRequired={false}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={disabled || !values?.item_batch_number}
                                    name="item_batch_discount_percentage"
                                    placeholder="Discount %"
                                    label="Discount %"
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                    handleChange={(e) => {
                                      let v = parseFloat(e.target.value);
                                      if (Number.isNaN(v)) v = 0;
                                      if (v < 0) v = 0;
                                      if (v > 100) v = 100;
                                      setFieldValue("item_batch_discount_percentage", v);
                                    }}
                                    onKeyDown={(e) => {
                                      decimalNumberFn(e);
                                      if (e.key === "Tab" || e.key === "Enter") {
                                        const v = Number(e.currentTarget.value || 0);

                                        handleQuantityChange(
                                          +values?.item_batch_quantity || 0,
                                          Number(values?.item_batch_sales_rate || 0),
                                          v / 100,
                                          parseInt(values?.item_batch_for_qauntity || 0),
                                          parseInt(values?.item_batch_for_free_qauntity || 0),
                                          parseInt(values?.item_batch_free_quantity || 0),
                                          setValues,
                                          values,
                                          Number(values?.item_batch_scheme_discount_percentage || 0),
                                          Number(values?.item_batch_unit_price || 0),
                                          values?.gst_type
                                        );
                                      }
                                    }}
                                    onBlur={(e) => {
                                      const v = Number(e.currentTarget.value || 0);

                                      handleQuantityChange(
                                        +values?.item_batch_quantity || 0,
                                        Number(values?.item_batch_sales_rate || 0),
                                        v / 100,
                                        parseInt(values?.item_batch_for_qauntity || 0),
                                        parseInt(values?.item_batch_for_free_qauntity || 0),
                                        parseInt(values?.item_batch_free_quantity || 0),
                                        setValues,
                                        values,
                                        Number(values?.item_batch_scheme_discount_percentage || 0),
                                        Number(values?.item_batch_unit_price || 0),
                                        values?.gst_type
                                      );
                                    }}
                                  />

                                  <InputField
                                    defaultInputClass={defaultInputClass}
                                    labelClass={labelClass}
                                    errorBorder={errorBorder}
                                    inputBorder={inputBorder}
                                    type="number"
                                    isRequired={false}
                                    containerClass="w-[50%]"
                                    parentClass="flex flex-col gap-2"
                                    isDisabled={true}
                                    name="item_batch_tax_percentage"
                                    placeholder="Tax %"
                                    label="Tax %"
                                    labelWidth="6.5rem"
                                    errMsgContainerClass="hidden"
                                  />
                                </div>
                                <div className="flex flex-col gap-1">
                                  <label className="text-xs font-medium text-gray-600">
                                    GST - Inter & Intra
                                  </label>
                                  <div className="flex items-center gap-6 mt-1">
                                    <label className="flex items-center gap-2 cursor-not-allowed opacity-60">
                                      <Field
                                        type="radio"
                                        name="gst_type"
                                        value="Intra"
                                        className="hidden peer"
                                        disabled
                                        checked={(itemValues?.is_inter_state !== true)}
                                      />
                                      <span className="w-3 h-3 rounded-full border border-gray-400 peer-checked:border-primary peer-checked:bg-primary transition-colors"></span>
                                      <span className="text-sm text-gray-800">Intra</span>
                                    </label>

                                    <label className="flex items-center gap-2 cursor-not-allowed opacity-60">
                                      <Field
                                        type="radio"
                                        name="gst_type"
                                        value="Inter"
                                        className="hidden peer"
                                        disabled
                                        checked={(itemValues?.is_inter_state === true)}
                                      />
                                      <span className="w-3 h-3 rounded-full border border-gray-400 peer-checked:border-primary peer-checked:bg-primary transition-colors"></span>
                                      <span className="text-sm text-gray-800">Inter</span>
                                    </label>
                                  </div>
                                </div>
                                {(itemValues?.is_inter_state !== true) && (
                                  <div className="flex gap-5">
                                    <InputField
                                      maxValue={100}
                                      defaultInputClass={defaultInputClass}
                                      labelClass={labelClass}
                                      errorBorder={errorBorder}
                                      inputBorder={inputBorder}
                                      type="number"
                                      isRequired={false}
                                      containerClass="w-[50%]"
                                      parentClass="flex flex-col gap-2"
                                      isDisabled={false}
                                      name="item_batch_cgst_percentage"
                                      placeholder="CGST %"
                                      label="CGST %"
                                      labelWidth="6.5rem"
                                      errMsgContainerClass="hidden"
                                    />
                                    <InputField
                                      maxValue={100}
                                      defaultInputClass={defaultInputClass}
                                      labelClass={labelClass}
                                      errorBorder={errorBorder}
                                      inputBorder={inputBorder}
                                      type="number"
                                      isRequired={false}
                                      containerClass="w-[50%]"
                                      parentClass="flex flex-col gap-2"
                                      isDisabled={false}
                                      name="item_batch_sgst_percentage"
                                      placeholder="SGST %"
                                      label="SGST %"
                                      labelWidth="6.5rem"
                                      errMsgContainerClass="hidden"
                                    />
                                  </div>
                                )}

                                {(itemValues?.is_inter_state === true) && (
                                  <div className="flex gap-5">
                                    <InputField
                                      maxValue={100}
                                      defaultInputClass={defaultInputClass}
                                      labelClass={labelClass}
                                      errorBorder={errorBorder}
                                      inputBorder={inputBorder}
                                      type="number"
                                      isRequired={false}
                                      containerClass="w-[50%]"
                                      parentClass="flex flex-col gap-2"
                                      isDisabled={false}
                                      name="item_batch_igst_percentage"
                                      placeholder="IGST %"
                                      label="IGST %"
                                      labelWidth="6.5rem"
                                      errMsgContainerClass="hidden"
                                    />
                                  </div>
                                )}
                              </div>
                              {/* Summary box - same keys as purchase */}
                              <div className="border border-[var(--slide-panel-border)] h-fit p-3 flex flex-col border-radius">
                                {[
                                  {
                                    label: "Final SP (Per 1 Qty excl Tax)",
                                    value: values?.item_batch_purhcase_rate_before_tax || 0,
                                  },
                                  {
                                    label: "Final SP (Per 1 Qty inc Tax)",
                                    value: values?.item_batch_final_sales_rate || 0,
                                  },
                                  {
                                    label: "Total Base SP",
                                    value:
                                      values?.item_batch_total_base_sales_rate ||
                                      values?.item_batch_total_sales_rate ||
                                      0,
                                  },
                                  {
                                    label: "Total Discount Amount",
                                    value: values?.item_batch_discount_amount || 0,
                                  },
                                  {
                                    label: "Taxable Amount",
                                    value:
                                      values?.item_batch_discounted_sales_rate ||
                                      values?.item_batch_taxable_amount ||
                                      0,
                                  },
                                  {
                                    label: "Tax Amount",
                                    value: values?.item_batch_tax_amount || 0,
                                  },
                                  {
                                    label: "SGST",
                                    value:
                                      
                                      values?.item_batch_sgst_amount
                                        ? values.item_batch_sgst_amount
                                        : 0,
                                  },
                                  {
                                    label: "CGST",
                                    value:
                                      
                                      values?.item_batch_cgst_amount
                                        ? values.item_batch_cgst_amount
                                        : 0,
                                  },
                                  {
                                    label: "IGST",
                                    value:
                                      
                                      values?.item_batch_igst_amount
                                        ? values.item_batch_igst_amount
                                        : 0,
                                  },
                                  {
                                    label: "Payable or Total Amt",
                                    value: values?.item_batch_total_sales_rate || 0,
                                  },
                                ].map((sum, idx) => (
                                  <div
                                    key={idx}
                                    className={`flex justify-between ${
                                      idx === 9 ? "" : " border-b border-b-[var(--slide-panel-border)]"
                                    } p-3`}
                                  >
                                    <span
                                      className={`text-[var(--payment-summary-label-fg)] text-[length:var(--payment-summary-label-fs)] font-[var(--payment-summary-label-fw)] ${
                                        ["IGST", "CGST", "SGST"].includes(sum?.label)
                                          ? "ml-[30px]"
                                          : ""
                                      }`}
                                    >
                                      {sum?.label}
                                      {["IGST", "CGST", "SGST"].includes(sum?.label) && (
                                        <sup className="text-[10px] text-[#374151] bg-[#e5e7eb] rounded-full px-[5px] py-[1px] ml-1 align-super">
                                          i
                                        </sup>
                                      )}
                                    </span>
                                    <span className="text-[var(--payment-summary-value-fg)] text-[length:var(--payment-summary-value-fs)] font-[var(--payment-summary-value-fw)]">
                                      ₹ {sum.value}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : (
                          // -------- Batch Info Stage --------
                          <div
                            style={{
                              height: `calc(100% - 12rem)`,
                              minHeight: `calc(100% - 12rem)`,
                            }}
                            className="px-4 pt-4 relative"
                          >
                            <div className="h-[2rem] mb-[1rem] flex justify-between items-center">
                              <span className="text-[var(--slide-panel-h-fg)] text-[length:var(--slide-panel-h-fs)] font-[var(--slide-panel-h-fw)]">
                                Batches For {defaultSelectedItem?.item_name}
                              </span>

                              {!disabled && remainingQty > 0 && (
                                <AddButton
                                  onclkFn={() => startAddBatch(setValues, values)}
                                  prefixIcon={false}
                                  text={`Add Batch (Remaining ${remainingQty})`}
                                  className="border rounded-[6px] border-[var(--status-badge-blue-border)] text-[length:--status-badge-special-fs] font-[var(--status-badge-fw)] text-[var(--status-badge-blue-fg)] bg-[var(--status-badge-blue-bg)] px-3 h-[36px]"
                                  type="button"
                                />
                              )}
                            </div>

                            <div
                              className="overflow-y-auto"
                              style={{
                                height: `calc(100% - 3rem)`,
                                minHeight: `calc(100% - 3rem)`,
                              }}
                            >
                              {(batches || []).map((batch, batchIndex) => {
                                const isOpen = openBatchIndex === batchIndex;

                                return (
                                  <div
                                    key={batchIndex}
                                    className="mt-4 border border-[var(--popover-border-color)] rounded-[8px] bg-[var(--slide-panel-accordian-bg)] overflow-hidden"
                                  >
                                    <div
                                      className={`h-[44px] cursor-pointer flex justify-between px-3 ${
                                        isOpen
                                          ? "border-b border-b-[var(--popover-border-color)]"
                                          : ""
                                      }`}
                                      onClick={() => toggleBatch(batchIndex)}
                                    >
                                      <div className="flex h-full items-center gap-2">
                                        <Package
                                          size={"20px"}
                                          color="var(--slide-panel-label)"
                                        />
                                        <span className="text-[var(--slide-panel-value)] text-[length:var(--slide-panel-value-fs)] font-[var(--slide-panel-value-fw)]">
                                          {batch.item_batch_number}
                                        </span>
                                      </div>

                                      <div className="flex items-center gap-2 text-[var(--slide-panel-value)] text-[length:var(--slide-panel-value-fs)] font-[var(--slide-panel-value-fw)]">
                                        Qty: {batch.item_batch_quantity}
                                        <ChevronDownIcon
                                          className={`transition-transform duration-200 ${
                                            isOpen ? "rotate-180" : ""
                                          }`}
                                        />
                                      </div>
                                    </div>

                                    {isOpen && (
                                      <div className="p-3 bg-[var(--slide-panel-accordian-open-bg)]">
                                        <div className="flex flex-wrap">
                                          {[
                                            { label: "From Bin", value: batch?.from_bin_location || "-" },
                                            { label: "Qty", value: batch?.item_batch_quantity || 0 },
                                            { label: "Free Qty", value: batch?.item_batch_free_quantity || 0 },
                                            { label: "MRP", value: batch?.item_batch_unit_price || 0 },
                                            { label: "Base SP", value: batch?.item_batch_sales_rate || 0 },
                                            { label: "Discount %", value: batch?.item_batch_discount_percentage || 0 },
                                            { label: "Tax %", value: batch?.item_batch_tax_percentage || 0 },
                                            { label: "GST Type", value: itemValues?.is_inter_state === true ? "Inter" : "Intra" },
                                            ...(itemValues?.is_inter_state !== true ? [
                                              { label: "CGST %", value: batch?.item_batch_cgst_percentage || 0 },
                                              { label: "SGST %", value: batch?.item_batch_sgst_percentage || 0 },
                                            ] : [
                                              { label: "IGST %", value: batch?.item_batch_igst_percentage || 0 },
                                            ]),
                                          ].map((bat, idx) => (
                                            <div
                                              key={idx}
                                              className="flex flex-col gap-1 w-[50%] mt-[1rem]"
                                            >
                                              <div className="text-[var(--slide-panel-label)] text-[length:var(--slide-panel-label-fs)] font-[var(--slide-label-value-fw)]">
                                                {bat.label}
                                              </div>
                                              <div className="text-[var(--slide-panel-value)] text-[length:var(--slide-panel-value-fs)] font-[var(--slide-panel-value-fw)]">
                                                {bat.value}
                                              </div>
                                            </div>
                                          ))}
                                        </div>

                                        <div className="border border-[var(--slide-panel-border)] h-fit p-3 flex flex-col border-radius mt-[1rem]">
                                          {[
                                            {
                                              label: "Base SP (Per 1 Qty)",
                                              value: batch?.item_batch_final_sales_rate || 0,
                                            },
                                            {
                                              label: "Total Base SP",
                                              value: batch?.item_batch_total_base_sales_rate || 0,
                                            },
                                            {
                                              label: "Total Discount Amount",
                                              value: batch?.item_batch_discount_amount || 0,
                                            },
                                            {
                                              label: "Taxable Amount",
                                              value: batch?.item_batch_discounted_sales_rate || 0,
                                            },
                                            {
                                              label: "Tax Amount",
                                              value: batch?.item_batch_tax_amount || 0,
                                            },
                                            {
                                              label: "SGST",
                                              value: batch?.item_batch_sgst_amount || 0,
                                              isGst: true,
                                            },
                                            {
                                              label: "CGST",
                                              value: batch?.item_batch_cgst_amount || 0,
                                              isGst: true,
                                            },
                                            {
                                              label: "IGST",
                                              value: batch?.item_batch_igst_amount || 0,
                                              isGst: true,
                                            },
                                            {
                                              label: "Payable or Total Amt",
                                              value: batch?.item_batch_total_sales_rate || 0,
                                            },
                                          ].map((sum, index, arr) => (
                                            <div
                                              key={index}
                                              className={`flex justify-between p-2 ${
                                                index === arr.length - 1
                                                  ? "border-t border-t-[var(--slide-panel-border)]"
                                                  : ""
                                              }`}
                                            >
                                              <span
                                                className={`text-[var(--payment-summary-label-fg)] text-[length:var(--payment-summary-label-fs)] font-[var(--payment-summary-label-fw)] ${
                                                  sum?.isGst ? "ml-[30px]" : ""
                                                }`}
                                              >
                                                {sum.label}
                                                {sum?.isGst && (
                                                  <sup className="text-[10px] text-[#374151] bg-[#e5e7eb] rounded-full px-[5px] py-[1px] ml-1 align-super">
                                                    i
                                                  </sup>
                                                )}
                                              </span>
                                              <span className="text-[var(--payment-summary-value-fg)] text-[length:var(--payment-summary-value-fs)] font-[var(--payment-summary-value-fw)]">
                                                ₹ {sum.value}
                                              </span>
                                            </div>
                                          ))}
                                        </div>

                                        {!disabled && (
                                          <div className="flex justify-between mt-[1rem]">
                                            <AddButton
                                              onclkFn={() => handleDeleteBatch(batchIndex)}
                                              prefixIcon={false}
                                              text={"Delete"}
                                              className="border rounded-[6px] border-[var(--status-badge-red-border)] text-[length:--status-badge-special-fs] font-[var(--status-badge-fw)] text-[var(--status-badge-red-fg)] bg-[var(--status-badge-red-bg)] px-3 h-[36px]"
                                              type="button"
                                            />

                                            <AddButton
                                              onclkFn={() => {
                                                setOpenBatchIndex(batchIndex);
                                                setNewBatch(true);
                                                setValues(
                                                  hydrateEditBatch(
                                                    batch,
                                                    defaultSelectedItem?.itemBatchList || []
                                                  )
                                                );
                                              }}
                                              prefixIcon={false}
                                              text={"Edit Batch"}
                                              className="border rounded-[6px] border-[var(--status-badge-blue-border)] text-[length:--status-badge-special-fs] font-[var(--status-badge-fw)] text-[var(--status-badge-blue-fg)] bg-[var(--status-badge-blue-bg)] px-3 h-[36px]"
                                              type="button"
                                            />
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Bottom button */}
                        <div className="h-[5rem] absolute bottom-0 bg-white flex justify-between items-center px-3 w-full">
                          <div />
                          <AddButton
                            onclkFn={() => {
                              if (newBatch) {
                                handleSubmit();
                                showFirstErrorToast(errors);
                              } else {
                                // If not fully allocated, keep user inside and open Add Batch
                                if (!disabled && remainingQty > 0) {
                                  startAddBatch(setValues, values);
                                  return;
                                }

                                // fully allocated -> close
                                setOpen(false);
                                if (searchRef?.current) {
                                  setTimeout(() => searchRef.current.focus(), 0);
                                }
                              }
                            }}
                            prefixIcon={false}
                            text={
                              newBatch
                                ? typeof openBatchIndex === "number"
                                  ? "Update Batch"
                                  : "Add Batch"
                                : remainingQty > 0
                                ? `Add Batch (Remaining ${remainingQty})`
                                : "Done"
                            }
                            className="
                              flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
                              bg-[var(--primary-button-bg-color)] cursor-pointer
                              text-[var(--primary-button-fg-color)]
                              border border-[var(--primary-button-bg-color)]
                              hover:bg-[var(--primary-button-hover-color)]
                              text-[length:var(--primary-button-font-size)]
                              font-[var(--primary-button-font-weight)]
                            "
                            type="button"
                          />
                        </div>
                      </Form>
                    );
                  }}
                </Formik>
              </Dialog.Panel>
            </Transition.Child>
          </div>

          <LoadingOverlay isLoading={loading} />
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default SalesBatchesPanel;
